import * as Sentry from '@sentry/react';

const SENTRY_DSN = "https://b6bc1335cbf96a39547c9b2159d246be@o4510052993335296.ingest.de.sentry.io/4510052996743248";
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

export const initSentry = () => {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `crm-app@${VERSION}`,

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    // Disable Sentry's default error dialog
    showDialog: false,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Disable masking for internal CRM - no public users, better debugging
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        // Keep default unblock/unmask selectors for compatibility
        unblock: [".sentry-unblock, [data-sentry-unblock]"],
        unmask: [".sentry-unmask, [data-sentry-unmask]"],
      }),
      Sentry.feedbackIntegration({
        // Disable auto-injected button, we'll use our own
        autoInject: false,

        // Form configuration
        isNameRequired: false,
        isEmailRequired: true,
        showScreenshot: true,
        showName: true,
        showBranding: false,
      })
    ],

    // Tracing
    tracesSampleRate: 1.0, // Capture 100% of the transactions

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      // Temporarily exclude Cloud Functions until CORS headers are updated
      // /^https:\/\/.*\.cloudfunctions\.net/,
      /^https:\/\/.*\.firebaseapp\.com/
    ],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Disable automatic crash dialogs and UI elements
    autoSessionTracking: false,

    // Errors from browser-extension scripts and other third-party noise
    ignoreErrors: [
      // Specific symbols injected by browser extensions
      'extDomain is not defined',
      // Generic noise from random extensions / injected scripts
      "Can't find variable: extDomain",
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Filter out scripts loaded from extension protocols
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^webkit-masked-url:\/\//i,
      /extensions\//i,
    ],

    // Error Filtering
    beforeSend(event, hint) {
      // Filter out errors from localhost domains
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')) {
          return null; // Ignore all errors from localhost
        }
      }

      // Drop errors whose stack trace is entirely <anonymous> with no real source
      // (browser extensions / injected userscripts that Sentry's global onerror
      // captures as if they were ours).
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (frames && frames.length > 0) {
        const allAnonymous = frames.every((f) => {
          const file = f.filename || '';
          return !file || file === '<anonymous>' || file.startsWith('chrome-extension://') || file.startsWith('moz-extension://');
        });
        if (allAnonymous) {
          return null;
        }
      }

      // Filter out known non-critical errors
      if (event.exception) {
        const error = hint.originalException;
        if (error && (error as Error).message && (error as Error).message.includes('ResizeObserver')) {
          return null; // Ignore ResizeObserver errors
        }
      }
      return event;
    },

    // Custom Tags
    initialScope: {
      tags: {
        component: 'crm-app',
        buildType: ENVIRONMENT,
      },
    },
  });
};

// User Context Helper
export const setSentryUser = (user: {
  id: string;
  email: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || user.email,
  });
};

// Custom Context Helper
export const setSentryContext = (context: Record<string, any>) => {
  Sentry.setContext('custom', context);
};

// Manual Error Reporting
export const reportError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach((key) => {
        scope.setTag(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};